// Centralized validation rules library for form inputs
// Uses atomic negative validations - blocks specific categories, allows everything else
/**
 * Centralized registry of all input validation rules
 *
 * Philosophy: By default, all input is allowed. Use atomic "no" rules to block specific
 * Unicode categories, then compose them for common use cases.
 *
 * Usage:
 * - Use rule name in Form's inputRules prop: inputRules={['email', 'minLength:2']}
 * - Rules with parameters use colon syntax: 'minLength:5', 'startsWithChar:A'
 * - Compose atomic rules: inputRules={['noEmojis', 'noSymbols']}
 */
export const inputRules = {
    // ============================================================================
    // CUSTOM VALIDATIONS
    // ============================================================================
    /**
     * Validates email address format
     * Usage: inputRules={['email']}
     */
    email: {
        validator: (value) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(value);
        },
        errorMessage: () => 'Please enter a valid email address',
    },
    /**
     * Blocks all punctuation except common name punctuation marks
     * Blocks: ! ? ; : ( ) [ ] { } / \ etc.
     * Allows: apostrophe ('), period (.), comma (,), hyphen (-), quotation marks (")
     * Based on noPunctuation but allows specific marks for names
     * Good for player names that should allow "O'Brien", "Dr. Smith", "Mary-Jane", etc.
     * Usage: inputRules={['allowNamePunctuation']}
     */
    noPunctuationExceptName: {
        validator: (value) => {
            // Remove all allowed name punctuation marks
            const allowedPunctuation = /['.,\-"]/g;
            const valueWithoutAllowedPunctuation = value.replace(allowedPunctuation, '');
            // Check if any Unicode punctuation remains (which would be disallowed)
            return !/[\p{P}]/u.test(valueWithoutAllowedPunctuation);
        },
        errorMessage: () => 'No special punctuation allowed',
    },
    /**
     * Blocks repeated characters (same character N+ times in a row)
     * Usage: inputRules={['noRepeatedChars:2']} or inputRules={['noRepeatedChars']} (default: 3)
     * Blocks: "aaaa", "ppppppp", "_______", etc.
     */
    noRepeatedChars: {
        validator: (value, params) => {
            const maxRepeats = params ? parseInt(params, 10) : 3;
            const regex = new RegExp(`(.)\\1{${maxRepeats},}`);
            return !regex.test(value);
        },
        errorMessage: () => {
            return `Same character repeated too many times`;
        },
    },
    /**
     * Validates that value starts with specific character (case-insensitive)
     * Usage: inputRules={['startsWithChar:A']}
     */
    startsWithChar: {
        validator: (value, params) => {
            if (!params || value.length === 0)
                return false;
            return value[0].toUpperCase() === params.toUpperCase();
        },
        errorMessage: (params) => {
            return `Must start with "${params || ''}"`;
        },
    },
    /**
     * Validates that value does not equal a provided comparison string after
     * trimming and case folding.
     * Usage: inputRules={[`notEqual:${encodeURIComponent(value)}`]}
     */
    notEqual: {
        validator: (value, params) => {
            if (!params)
                return true;
            let comparison = params;
            try {
                comparison = decodeURIComponent(params);
            }
            catch {
                comparison = params;
            }
            return value.trim().toLowerCase() !== comparison.trim().toLowerCase();
        },
        errorMessage: () => 'Must be different from the prompt',
    },
    /**
     * Blocks reserved host names (e.g. Poppy and common lookalike spellings).
     * Only for manually typed names — Twitch login names skip this rule upstream.
     * Usage: inputRules={['noReservedHostName']}
     */
    noReservedHostName: {
        validator: (value) => {
            const reservedManualPlayerNames = new Set([
                'poppy',
                'poppie',
                'poppi',
                'popppy',
                'poppey',
                'popy',
                'poppei',
                'poppe',
                'poppyy',
                'poppyyy',
                'popyyy',
                'poppee',
                'poppeee',
            ]);
            return !reservedManualPlayerNames.has(value.trim().toLowerCase());
        },
        errorMessage: () => 'That name is reserved',
    },
    // ============================================================================
    // LENGTH VALIDATIONS
    // ============================================================================
    /**
     * Validates minimum length
     * Usage: inputRules={['minLength:2']}
     */
    minLength: {
        validator: (value, params) => {
            const min = params ? parseInt(params, 10) : 1;
            return value.length >= min;
        },
        errorMessage: (params) => {
            const min = params || '1';
            return `Must be at least ${min} character${min === '1' ? '' : 's'}`;
        },
    },
    /**
     * Validates maximum length
     * Usage: inputRules={['maxLength:12']}
     */
    maxLength: {
        validator: (value, params) => {
            const max = params ? parseInt(params, 10) : 50;
            return value.length <= max;
        },
        errorMessage: (params) => {
            const max = params || '50';
            return `Must be ${max} character${max === '1' ? '' : 's'} or less`;
        },
    },
    // ============================================================================
    // ATOMIC VALIDATIONS (Block specific Unicode categories)
    // Philosophy: Block what you don't want, allow everything else
    // ============================================================================
    /**
     * Blocks all letters (Unicode category: \p{L})
     * Blocks: A-Z, a-z, é, ñ, ü, 中文, العربية, Русский, etc.
     * Usage: inputRules={['noLetters']}
     */
    noLetters: {
        validator: (value) => {
            return !/[\p{L}]/u.test(value);
        },
        errorMessage: () => 'Letters not allowed',
    },
    /**
     * Blocks all numbers (Unicode category: \p{N})
     * Blocks: 0-9, ١٢٣ (Arabic), ๑๒๓ (Thai), etc.
     * Usage: inputRules={['noNumbers']}
     */
    noNumbers: {
        validator: (value) => {
            return !/[\p{N}]/u.test(value);
        },
        errorMessage: () => 'Numbers not allowed',
    },
    /**
     * Special case for convenience:
     * Allows only numbers (Unicode category: \p{N})
     * Allows: 0-9, ١٢٣ (Arabic), ๑๒๓ (Thai), etc.
     * Usage: inputRules={['onlyNumbers']}
     */
    onlyNumbers: {
        validator: (value) => {
            return /[\p{N}]/u.test(value);
        },
        errorMessage: () => 'Only numbers allowed',
    },
    /**
     * Blocks all separator characters (Unicode category: \p{Z})
     * Blocks: spaces, non-breaking spaces, line separators, etc.
     * Usage: inputRules={['noSpaces']}
     */
    noSpaces: {
        validator: (value) => {
            return !/[\p{Z}]/u.test(value);
        },
        errorMessage: () => 'Spaces not allowed',
    },
    /**
     * Blocks all punctuation (Unicode category: \p{P})
     * Blocks: . , ! ? ; : - ' " ( ) [ ] { } / \ etc.
     * Usage: inputRules={['noPunctuation']}
     */
    noPunctuation: {
        validator: (value) => {
            return !/[\p{P}]/u.test(value);
        },
        errorMessage: () => 'Punctuation not allowed',
    },
    /**
     * Blocks all symbols (Unicode category: \p{S})
     * Blocks: $, €, ©, ™, ±, ×, ÷, mathematical symbols, currency symbols, etc.
     * Note: This includes emojis. Use noEmojis if you only want to block emojis.
     * Usage: inputRules={['noSymbols']}
     */
    noSymbols: {
        validator: (value) => {
            return !/[\p{S}]/u.test(value);
        },
        errorMessage: () => 'Symbols not allowed',
    },
    /**
     * Blocks emojis and pictographic symbols
     * Blocks: 😀, 🎉, 👍, etc.
     * Usage: inputRules={['noEmojis']}
     */
    noEmojis: {
        validator: (value) => {
            const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
            return !emojiRegex.test(value);
        },
        errorMessage: () => 'Emojis not allowed',
    },
    /**
     * Blocks control characters (Unicode category: \p{C})
     * Blocks: null, tab, newline, carriage return, etc.
     * Usage: inputRules={['noControl']}
     */
    noControl: {
        validator: (value) => {
            return !/[\p{C}]/u.test(value);
        },
        errorMessage: () => 'Control characters not allowed',
    },
    /**
     * Blocks combining marks (Unicode category: \p{M})
     * Blocks: accents when separated from letters (◌́, ◌̀, etc.)
     * Usage: inputRules={['noMarks']}
     */
    noMarks: {
        validator: (value) => {
            return !/[\p{M}]/u.test(value);
        },
        errorMessage: () => 'Combining marks not allowed',
    },
};
/**
 * Helper function to run a validation rule
 * @param ruleName - Name of the rule (e.g., 'email', 'minLength:2')
 * @param value - Value to validate
 * @returns Error message if validation fails, null if passes
 */
export function validateInput(ruleName, value) {
    const separatorIndex = ruleName.indexOf(':');
    const name = separatorIndex === -1 ? ruleName : ruleName.slice(0, separatorIndex);
    const params = separatorIndex === -1 ? undefined : ruleName.slice(separatorIndex + 1);
    const rule = inputRules[name];
    if (!rule) {
        console.warn(`Unknown validation rule: ${name}`);
        return null;
    }
    const isValid = rule.validator(value, params);
    return isValid ? null : rule.errorMessage(params);
}
/**
 * Helper function to run multiple validation rules
 * @param ruleNames - Array of rule names
 * @param value - Value to validate
 * @returns First error message encountered, or null if all pass
 */
export function validateInputMultiple(ruleNames, value) {
    for (const ruleName of ruleNames) {
        const error = validateInput(ruleName, value);
        if (error)
            return error;
    }
    return null;
}
//# sourceMappingURL=inputRules.js.map